import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import {
  Mail, Search, Download, Trash2, UserX, UserCheck, Loader2, Inbox, UserPlus, Check,
} from 'lucide-react';
import api from '../lib/api';
import { PageHeader } from '../components/layout/PageHeader';
import { EmptyState } from '../components/layout/EmptyState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { downloadCsv } from '../lib/csv';

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

const StatCard = ({ value, label, accent }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={`subscribers-stat-${label.toLowerCase()}`}>
    <div className={`font-display text-2xl font-semibold ${accent || 'text-foreground'}`}>{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>
);

export default function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [contactEmails, setContactEmails] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [subRes, contactRes] = await Promise.all([
        api.get('/signups'),
        api.get('/contacts').catch(() => ({ data: [] })),
      ]);
      setSubs(subRes.data.map((s) => ({ ...s, status: s.status || 'active' })));
      setContactEmails(new Set((contactRes.data || []).map((c) => (c.email || '').toLowerCase())));
    } catch (e) { toast.error('Could not load subscribers'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const counts = useMemo(() => ({
    all: subs.length,
    active: subs.filter((s) => s.status === 'active').length,
    declined: subs.filter((s) => s.status === 'declined').length,
    unsubscribed: subs.filter((s) => s.status === 'unsubscribed').length,
  }), [subs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subs.filter((s) => {
      if (tab !== 'all' && s.status !== tab) return false;
      if (!q) return true;
      return (s.email || '').toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q);
    });
  }, [subs, search, tab]);

  const setStatus = async (s, status) => {
    setBusyId(s.id);
    try {
      await api.put(`/signups/${s.id}/status`, { status });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, status } : x)));
      toast.success(status === 'declined' ? 'Subscriber declined' : 'Subscriber reactivated');
    } catch (e) { toast.error('Could not update subscriber'); }
    finally { setBusyId(null); }
  };

  const remove = async () => {
    const s = confirmDelete;
    if (!s) return;
    setBusyId(s.id);
    try {
      await api.delete(`/signups/${s.id}`);
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
      toast.success('Subscriber deleted');
    } catch (e) { toast.error('Could not delete subscriber'); }
    finally { setBusyId(null); setConfirmDelete(null); }
  };

  const addToContacts = async (s) => {
    setBusyId(s.id);
    try {
      const r = await api.post(`/signups/${s.id}/convert`);
      setContactEmails((prev) => new Set(prev).add((s.email || '').toLowerCase()));
      toast.success(r.data.created ? `${s.name || s.email} added to contacts` : 'Already in your contacts');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add to contacts'); }
    finally { setBusyId(null); }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkAddToContacts = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post('/signups/convert-bulk', { ids });
      setContactEmails((prev) => {
        const n = new Set(prev);
        (r.data.emails || []).forEach((e) => n.add(e));
        return n;
      });
      const { created, skipped } = r.data;
      toast.success(
        `${created} added to contacts${skipped ? ` · ${skipped} already there` : ''}`
      );
      clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to contacts');
    } finally { setBulkBusy(false); }
  };

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error('Nothing to export'); return; }
    const headers = [
      { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
      { key: 'status', label: 'Status' }, { key: 'created_at', label: 'Joined' },
    ];
    const rows = filtered.map((s) => ({
      name: s.name || '', email: s.email || '', status: s.status || 'active',
      created_at: fmtDate(s.created_at),
    }));
    downloadCsv('thrive-subscribers', headers, rows);
    toast.success(`Exported ${filtered.length} subscribers`);
  };

  return (
    <div data-testid="subscribers-page">
      <PageHeader title="Subscribers" subtitle="People who joined your email list from public sign-up pages. Review, decline, or remove anyone.">
        <Button variant="secondary" onClick={exportCsv} className="gap-2" data-testid="subscribers-export-button"><Download className="h-4 w-4" /> Export</Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard value={counts.all} label="Total" />
        <StatCard value={counts.active} label="Active" accent="text-primary" />
        <StatCard value={counts.declined} label="Declined" accent="text-muted-foreground" />
        <StatCard value={counts.unsubscribed} label="Unsubscribed" accent="text-accent" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
          <TabsList data-testid="subscribers-tabs">
            <TabsTrigger value="all" data-testid="tab-all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Active ({counts.active})</TabsTrigger>
            <TabsTrigger value="declined" data-testid="tab-declined">Declined ({counts.declined})</TabsTrigger>
            <TabsTrigger value="unsubscribed" data-testid="tab-unsubscribed">Unsubscribed ({counts.unsubscribed})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="pl-9 h-11" data-testid="subscribers-search" />
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2" data-testid="subscribers-bulk-bar">
          <span className="text-sm font-medium text-primary px-1">{selected.size} selected</span>
          <Button size="sm" className="gap-1.5 h-9" disabled={bulkBusy} onClick={bulkAddToContacts} data-testid="bulk-add-contacts-button">
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Add to contacts
          </Button>
          <Button size="sm" variant="ghost" className="h-9" onClick={clearSelection} data-testid="bulk-clear-button">Clear</Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card">
          <EmptyState icon={subs.length === 0 ? Inbox : Search}
            title={subs.length === 0 ? 'No subscribers yet' : 'No matches'}
            description={subs.length === 0
              ? 'When people join your email list from a public sign-up page, they will show up here.'
              : 'Try a different search or filter.'} />
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="subscribers-list">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-secondary/30">
            <Checkbox
              checked={filtered.length > 0 && filtered.every((s) => selected.has(s.id))}
              onCheckedChange={() => setSelected((prev) => {
                const allSel = filtered.length > 0 && filtered.every((s) => prev.has(s.id));
                const n = new Set(prev);
                filtered.forEach((s) => (allSel ? n.delete(s.id) : n.add(s.id)));
                return n;
              })}
              data-testid="subscribers-select-all"
            />
            <span className="text-xs text-muted-foreground">Select all ({filtered.length})</span>
          </div>
          {filtered.map((s) => (
            <div key={s.id} className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 transition-colors ${selected.has(s.id) ? 'bg-primary/5' : ''}`} data-testid="subscriber-row">
              <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleOne(s.id)} data-testid="subscriber-checkbox" />
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4.5 w-4.5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground truncate">{s.name || s.email}</div>
                <div className="text-sm text-muted-foreground truncate">{s.email}</div>
              </div>
              <div className="hidden sm:block text-sm text-muted-foreground w-28 text-right">{fmtDate(s.created_at)}</div>
              <div className="w-24 text-right">
                {s.status === 'unsubscribed'
                  ? <Badge variant="secondary" className="text-accent font-normal">Unsubscribed</Badge>
                  : s.status === 'declined'
                    ? <Badge variant="secondary" className="text-muted-foreground font-normal">Declined</Badge>
                    : <Badge variant="secondary" className="text-primary font-normal">Active</Badge>}
              </div>
              <div className="flex items-center gap-1">
                {contactEmails.has((s.email || '').toLowerCase()) ? (
                  <span className="hidden sm:inline-flex items-center gap-1 text-xs text-primary mr-1" data-testid="subscriber-in-contacts">
                    <Check className="h-3.5 w-3.5" /> In contacts
                  </span>
                ) : (
                  <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => addToContacts(s)} className="gap-1.5" data-testid="subscriber-add-contact" title="Add to contacts">
                    {busyId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}<span className="hidden sm:inline">Add to contacts</span>
                  </Button>
                )}
                {s.status === 'unsubscribed' ? (
                  <span className="hidden sm:inline text-xs text-muted-foreground mr-1" data-testid="subscriber-optout-note">Opted out</span>
                ) : s.status === 'declined' ? (
                  <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => setStatus(s, 'active')} className="gap-1.5" data-testid="subscriber-reactivate">
                    {busyId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}<span className="hidden sm:inline">Reactivate</span>
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => setStatus(s, 'declined')} className="gap-1.5" data-testid="subscriber-decline">
                    {busyId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}<span className="hidden sm:inline">Decline</span>
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(s)} className="text-muted-foreground hover:text-destructive" data-testid="subscriber-delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="subscriber-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete subscriber?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {confirmDelete?.email} from your email list. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="subscriber-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="subscriber-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
